import { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Title2,
  Body1,
  Tab,
  TabList,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Field,
  Input,
  Dropdown,
  Option,
  Switch,
  Spinner,
  MessageBar,
  MessageBarBody,
  Badge,
} from '@fluentui/react-components';
import { AddRegular, DeleteRegular } from '@fluentui/react-icons';
import { apiClient, ListResponse } from '../api/client';
import { DATA_LOCATIONS } from '../config/constants';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
  },
  tabs: {
    marginBottom: tokens.spacingVerticalL,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: tokens.spacingVerticalM,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: tokens.spacingHorizontalM,
  },
});

interface Site {
  _id: string;
  SITE_CODE: string;
  Company_Name: string;
  City: string;
  Country: string;
  PreferredDataLoc: string;
  active: boolean;
}

interface Software {
  _id: string;
  name: string;
  description: string;
  category?: string;
  requiresLicense: boolean;
  active: boolean;
}

type ActiveTab = 'sites' | 'software';

export function AdminPage() {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<ActiveTab>('sites');
  const [sites, setSites] = useState<Site[]>([]);
  const [software, setSoftware] = useState<Software[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Site | Software | null>(null);

  // Site form state
  const [siteForm, setSiteForm] = useState({
    SITE_CODE: '',
    PHONE: '',
    Company_Name: '',
    StreetAddress: '',
    City: '',
    State: '',
    PostalCode: '',
    Country: '',
    PreferredLanguage: 'en-US',
    PreferredDataLoc: 'EUR',
    UsageLocation: '',
  });

  // Software form state
  const [softwareForm, setSoftwareForm] = useState({
    name: '',
    description: '',
    category: '',
    requiresLicense: false,
  });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [sitesRes, softwareRes] = await Promise.all([
        apiClient.get<ListResponse<Site>>('/sites?active=false'),
        apiClient.get<ListResponse<Software>>('/software?active=false'),
      ]);
      setSites(sitesRes.data);
      setSoftware(softwareRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddSite = () => {
    setSiteForm({
      SITE_CODE: '',
      PHONE: '',
      Company_Name: '',
      StreetAddress: '',
      City: '',
      State: '',
      PostalCode: '',
      Country: '',
      PreferredLanguage: 'en-US',
      PreferredDataLoc: 'EUR',
      UsageLocation: '',
    });
    setEditItem(null);
    setDialogOpen(true);
  };

  const handleAddSoftware = () => {
    setSoftwareForm({
      name: '',
      description: '',
      category: '',
      requiresLicense: false,
    });
    setEditItem(null);
    setDialogOpen(true);
  };

  const handleSaveSite = async () => {
    try {
      if (editItem) {
        await apiClient.put(`/sites/${editItem._id}`, siteForm);
      } else {
        await apiClient.post('/sites', siteForm);
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save site');
    }
  };

  const handleSaveSoftware = async () => {
    try {
      if (editItem) {
        await apiClient.put(`/software/${editItem._id}`, softwareForm);
      } else {
        await apiClient.post('/software', softwareForm);
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save software');
    }
  };

  const handleDeleteSite = async (id: string) => {
    if (!confirm('Are you sure you want to deactivate this site?')) return;
    try {
      await apiClient.delete(`/sites/${id}`);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete site');
    }
  };

  const handleDeleteSoftware = async (id: string) => {
    if (!confirm('Are you sure you want to deactivate this software?')) return;
    try {
      await apiClient.delete(`/software/${id}`);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete software');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title2>Admin Panel</Title2>
        <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
          Manage sites and software options
        </Body1>
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <TabList
        className={styles.tabs}
        selectedValue={activeTab}
        onTabSelect={(_, data) => setActiveTab(data.value as ActiveTab)}
      >
        <Tab value="sites">Sites ({sites.length})</Tab>
        <Tab value="software">Software ({software.length})</Tab>
      </TabList>

      {loading ? (
        <div className={styles.loading}>
          <Spinner size="large" label="Loading..." />
        </div>
      ) : activeTab === 'sites' ? (
        <Card>
          <div className={styles.toolbar}>
            <Button appearance="primary" icon={<AddRegular />} onClick={handleAddSite}>
              Add Site
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Code</TableHeaderCell>
                <TableHeaderCell>Company</TableHeaderCell>
                <TableHeaderCell>Location</TableHeaderCell>
                <TableHeaderCell>Data Location</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sites.map((site) => (
                <TableRow key={site._id}>
                  <TableCell>{site.SITE_CODE}</TableCell>
                  <TableCell>{site.Company_Name}</TableCell>
                  <TableCell>{site.City}, {site.Country}</TableCell>
                  <TableCell>{site.PreferredDataLoc}</TableCell>
                  <TableCell>
                    <Badge color={site.active ? 'success' : 'danger'}>
                      {site.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      appearance="subtle"
                      icon={<DeleteRegular />}
                      onClick={() => handleDeleteSite(site._id)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <div className={styles.toolbar}>
            <Button appearance="primary" icon={<AddRegular />} onClick={handleAddSoftware}>
              Add Software
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Description</TableHeaderCell>
                <TableHeaderCell>Category</TableHeaderCell>
                <TableHeaderCell>License</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {software.map((sw) => (
                <TableRow key={sw._id}>
                  <TableCell>{sw.name}</TableCell>
                  <TableCell>{sw.description}</TableCell>
                  <TableCell>{sw.category || '-'}</TableCell>
                  <TableCell>
                    {sw.requiresLicense ? (
                      <Badge color="warning">Required</Badge>
                    ) : (
                      <Badge color="subtle">No</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge color={sw.active ? 'success' : 'danger'}>
                      {sw.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      appearance="subtle"
                      icon={<DeleteRegular />}
                      onClick={() => handleDeleteSoftware(sw._id)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(_, data) => setDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {activeTab === 'sites' ? 'Add Site' : 'Add Software'}
            </DialogTitle>
            <DialogContent>
              {activeTab === 'sites' ? (
                <div className={styles.form}>
                  <div className={styles.formRow}>
                    <Field label="Site Code" required>
                      <Input
                        value={siteForm.SITE_CODE}
                        onChange={(_, d) => setSiteForm({ ...siteForm, SITE_CODE: d.value })}
                      />
                    </Field>
                    <Field label="Phone" required>
                      <Input
                        value={siteForm.PHONE}
                        onChange={(_, d) => setSiteForm({ ...siteForm, PHONE: d.value })}
                      />
                    </Field>
                  </div>
                  <Field label="Company Name" required>
                    <Input
                      value={siteForm.Company_Name}
                      onChange={(_, d) => setSiteForm({ ...siteForm, Company_Name: d.value })}
                    />
                  </Field>
                  <Field label="Street Address" required>
                    <Input
                      value={siteForm.StreetAddress}
                      onChange={(_, d) => setSiteForm({ ...siteForm, StreetAddress: d.value })}
                    />
                  </Field>
                  <div className={styles.formRow}>
                    <Field label="City" required>
                      <Input
                        value={siteForm.City}
                        onChange={(_, d) => setSiteForm({ ...siteForm, City: d.value })}
                      />
                    </Field>
                    <Field label="State">
                      <Input
                        value={siteForm.State}
                        onChange={(_, d) => setSiteForm({ ...siteForm, State: d.value })}
                      />
                    </Field>
                  </div>
                  <div className={styles.formRow}>
                    <Field label="Postal Code" required>
                      <Input
                        value={siteForm.PostalCode}
                        onChange={(_, d) => setSiteForm({ ...siteForm, PostalCode: d.value })}
                      />
                    </Field>
                    <Field label="Country" required>
                      <Input
                        value={siteForm.Country}
                        onChange={(_, d) => setSiteForm({ ...siteForm, Country: d.value })}
                      />
                    </Field>
                  </div>
                  <div className={styles.formRow}>
                    <Field label="Data Location" required>
                      <Dropdown
                        value={DATA_LOCATIONS.find(l => l.key === siteForm.PreferredDataLoc)?.text || ''}
                        onOptionSelect={(_, d) =>
                          setSiteForm({ ...siteForm, PreferredDataLoc: d.optionValue as string })
                        }
                      >
                        {DATA_LOCATIONS.map((loc) => (
                          <Option key={loc.key} value={loc.key}>{loc.text}</Option>
                        ))}
                      </Dropdown>
                    </Field>
                    <Field label="Usage Location (ISO)" required>
                      <Input
                        value={siteForm.UsageLocation}
                        onChange={(_, d) => setSiteForm({ ...siteForm, UsageLocation: d.value.toUpperCase() })}
                        maxLength={2}
                        placeholder="US, MX, CA..."
                      />
                    </Field>
                  </div>
                </div>
              ) : (
                <div className={styles.form}>
                  <Field label="Name" required>
                    <Input
                      value={softwareForm.name}
                      onChange={(_, d) => setSoftwareForm({ ...softwareForm, name: d.value })}
                    />
                  </Field>
                  <Field label="Description" required>
                    <Input
                      value={softwareForm.description}
                      onChange={(_, d) => setSoftwareForm({ ...softwareForm, description: d.value })}
                    />
                  </Field>
                  <Field label="Category">
                    <Input
                      value={softwareForm.category}
                      onChange={(_, d) => setSoftwareForm({ ...softwareForm, category: d.value })}
                      placeholder="e.g., Productivity, Design..."
                    />
                  </Field>
                  <Field label="Requires License">
                    <Switch
                      checked={softwareForm.requiresLicense}
                      onChange={(_, d) => setSoftwareForm({ ...softwareForm, requiresLicense: d.checked })}
                    />
                  </Field>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={activeTab === 'sites' ? handleSaveSite : handleSaveSoftware}
              >
                Save
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
